import { useEffect, useState } from "react";
import { fetchSiteImages } from "../api/siteImagesApi";

// scripts/generate-embedded-data.js bakes the site-images map into every
// built page (<script id="site-images-data" type="application/json">), so
// components can render their final images on the first paint instead of
// swapping them in after the API call -- that swap was a large layout shift on
// the feature pages. The API is still called once to pick up images an admin
// changed after the build.
function readEmbeddedSiteImages() {
  try {
    const el = document.getElementById("site-images-data");
    return el ? JSON.parse(el.textContent) : null;
  } catch {
    return null;
  }
}

const embeddedSiteImages = readEmbeddedSiteImages();

// Module-level cache/promise so the API is only ever hit once for the whole
// page lifetime, no matter how many components call this hook (e.g. the
// brand logo shows up on nearly every page) — avoids needing a top-level
// <Provider> wrapper around <App/>.
let cachedPromise = null;

function getSiteImages() {
  if (!cachedPromise) {
    cachedPromise = fetchSiteImages().catch(() => embeddedSiteImages || {});
  }
  return cachedPromise;
}

/**
 * Returns the admin-managed site images map ({ key: { url, alt } }) plus
 * whether it has loaded (true immediately when the page embeds it). Use when
 * a component must not render a stand-in that the real image would replace.
 */
export function useSiteImagesStatus() {
  const [state, setState] = useState(() => ({
    images: embeddedSiteImages || {},
    loaded: Boolean(embeddedSiteImages),
  }));

  useEffect(() => {
    let cancelled = false;
    getSiteImages().then((data) => {
      if (!cancelled) setState({ images: data || {}, loaded: true });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}

/**
 * Returns the admin-managed site images map ({ key: { url, alt } }), or an
 * empty object until loaded / if the fetch fails. Always use with a static
 * fallback: siteImages?.some_key?.url || staticImportedImage
 */
export default function useSiteImages() {
  return useSiteImagesStatus().images;
}
